import { BRANCHES } from '~/assets/constants';
import { getBranchByCode } from '~/helpers/branch';
import { getDomainByBranch, getFallbackDomain } from '~/helpers/domain';
import { isAbsolutePath, getProtocol, buildUrl } from '~/helpers/routing';
import type { BranchCode, Image } from '~/types';

interface Props {
  title: string;
  description: string;
  image?: Required<Omit<Image, 'position' | 'desc'>>;
  type?: 'website' | 'article';
  alternateBranches?: BranchCode[] | 'all';
}

export default function usePageHead(props: Props) {
  const { type, image, title, description, alternateBranches } = props;
  const { buildUrl: buildCurrentDomainUrl } = useRouting();
  const { $domain } = useNuxtApp();
  const route = useRoute();
  const protocol = getProtocol($domain);

  const ogImageSrc = computed(() =>
    image ?
      (isAbsolutePath(image.src) ? image.src : buildCurrentDomainUrl(image.src)) :
      `${protocol}://${$domain}/api/og-image/screenshot?url=${protocol}://${$domain}${route.path}`
  );
  const ogImageWidth = computed(() => image?.width.toString() || '1600');
  const ogImageHeight = computed(() => image?.height.toString() || '800');

  const branches = (alternateBranches == 'all' ? BRANCHES : alternateBranches || []).map(getBranchByCode);

  useHead({
    title,

    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:type', content: type || 'website' },
      { property: 'og:description', content: description },
      { property: 'og:image', content: ogImageSrc.value },
      { property: 'og:image:width', content: ogImageWidth.value },
      { property: 'og:image:height', content: ogImageHeight.value },
    ],

    link: [
      ...branches.map(({ code, locale }) => {
        const domain = getDomainByBranch(code);
        return {
          rel: 'alternate',
          hreflang: locale,
          href: buildUrl(domain, route.path),
        };
      }),
      {
        rel: 'alternate',
        hreflang: 'x-default',
        href: buildUrl(getFallbackDomain(), route.path),
      },
    ]
  });
}